import React, { useState } from 'react';
import {
  ShieldCheck,
  Target,
  Users,
  EyeOff,
  Scale,
  CheckCircle2,
  XCircle,
  Building2,
  Lock,
  ArrowRight,
  UserCheck,
  ChevronRight,
  Briefcase
} from 'lucide-react';
import { LandingHeader } from './LandingHeader';
import LandingFooter from './LandingFooter';

interface NavSection {
  id: string;
  title: string;
  icon: React.ElementType;
}

const sections: NavSection[] = [
  { id: 'who-we-are', title: '1. Who We Are', icon: Building2 },
  { id: 'why-exists', title: '2. Why Nexgent Exists', icon: Target },
  { id: 'who-we-serve', title: '3. Who We Serve', icon: Users },
  { id: 'privacy-matters', title: '4. Privacy as a Priority', icon: EyeOff },
  { id: 'comparison', title: '5. Traditional vs Nexgent', icon: Scale },
  { id: 'our-approach', title: '6. Our Practical Approach', icon: ShieldCheck },
  { id: 'trust-security', title: '7. Trust & Verification', icon: Lock },
  { id: 'looking-ahead', title: '8. Looking Ahead', icon: ArrowRight },
];

export const AboutUs: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('who-we-are');

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-cyan-500 selection:text-slate-950 relative overflow-hidden">
      {/* Background Cyber Glow Effects */}
      <LandingHeader/>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-cyan-500/10 blur-[140px] pointer-events-none rounded-full" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-blue-600/10 blur-[150px] pointer-events-none rounded-full" />

      {/* Header Banner */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-mono font-medium bg-cyan-950 text-cyan-400 border border-cyan-800/50 rounded-full tracking-wide">
                ABOUT NEXGENT
              </span>
              <span className="text-xs text-slate-500">Motherson Infotech Division</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
              Purpose, Integrity & Platform Architecture
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-800">
            <ShieldCheck className="w-4 h-4 text-cyan-400" />
            <span>Curated Executive Ecosystem</span>
          </div>
        </div>
      </header>

      {/* Main Container Layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Navigation Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-24 bg-slate-900/70 border border-slate-800 rounded-xl p-4 backdrop-blur-md shadow-xl">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-2">
              Platform Overview Index
            </h2>
            <nav className="space-y-1">
              {sections.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium rounded-lg transition-all text-left ${
                      isActive
                        ? 'bg-cyan-950/60 text-cyan-300 border border-cyan-800/60 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                      <span className="truncate">{item.title}</span>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? 'text-cyan-400 translate-x-0.5' : 'text-slate-600'}`} />
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content Section */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-10">
          
          {/* Section 1 */}
          <section id="who-we-are" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Building2 className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">1. Who We Are</h2>
            </div>
            <p className="text-slate-300 leading-relaxed text-sm">
              <strong className="text-cyan-400">Nexgent</strong> (operated by Motherson Infotech) is a private leadership hiring platform designed to connect experienced professionals with verified recruiters and organizations.
            </p>
            <p className="text-slate-400 text-xs leading-relaxed">
              Unlike traditional job portals that prioritize large public databases and mass applications, Nexgent focuses on creating a curated, confidential, and highly focused hiring environment specifically engineered for senior talent and executive roles.
            </p>
          </section>

          {/* Section 2 */}
          <section id="why-exists" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Target className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">2. Why Nexgent Exists</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Many experienced professionals are open to strategic new opportunities but do not actively search on public job boards due to privacy concerns. Simultaneously, organizations often struggle to identify qualified executive leadership through standard public channels flooded with unverified applications.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1 text-xs">
              <span className="text-cyan-400 font-semibold font-mono uppercase">Bridging the Leadership Gap:</span>
              <p className="text-slate-400 leading-relaxed">
                Nexgent was established to solve this specific disconnect—providing a private infrastructure where senior talent and hiring entities can engage directly through a targeted, discrete, and professional hiring process.
              </p>
            </div>
          </section>

          {/* Section 3 */}
          <section id="who-we-serve" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Users className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">3. Who We Serve</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div className="p-4 bg-slate-950/70 border border-slate-800 rounded-xl space-y-2">
                <div className="flex items-center gap-2 text-cyan-400 font-bold text-xs font-mono uppercase">
                  <UserCheck className="w-4 h-4" /> Leadership Professionals
                </div>
                <ul className="text-xs text-slate-400 space-y-1.5 list-disc pl-4">
                  <li>Possess significant industry domain experience.</li>
                  <li>Are open to evaluating high-value career transitions.</li>
                  <li>Require absolute discretion and confidentiality.</li>
                  <li>Demand fine-grained control over their profile visibility.</li>
                </ul>
              </div>

              <div className="p-4 bg-slate-950/70 border border-slate-800 rounded-xl space-y-2">
                <div className="flex items-center gap-2 text-cyan-400 font-bold text-xs font-mono uppercase">
                  <Briefcase className="w-4 h-4" /> Recruiters & Employers
                </div>
                <ul className="text-xs text-slate-400 space-y-1.5 list-disc pl-4">
                  <li>Regularly hire experienced executive talent.</li>
                  <li>Require direct access to verified candidate profiles.</li>
                  <li>Value relevant intent data over volume.</li>
                  <li>Seek a structured, noise-free recruitment model.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section id="privacy-matters" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <EyeOff className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">4. Why Privacy Matters</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Career evaluation at the executive level requires utmost discretion. Broad public exposure of job-seeking activity can introduce professional complications for both candidates and employers.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl text-xs text-slate-400 space-y-2">
              <div className="text-cyan-400 font-semibold">Privacy as Architectural Core</div>
              <p className="leading-relaxed">
                Nexgent treats privacy not as an add-on feature, but as a foundational design parameter. Profile visibility controls, anonymized initial disclosures, and permissioned recruiter access ensure a controlled environment throughout every stage of interaction.
              </p>
            </div>
          </section>

        {/* Section 5 */}
        <section id="comparison" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Scale className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">5. How Nexgent Differs</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              A side-by-side overview of how our curated platform design compares against traditional open job portals:
            </p>
            
            <div className="overflow-x-auto pt-2">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400 font-mono">
                    <th className="py-3 px-4 font-medium w-1/4">Dimension</th>
                    <th className="py-3 px-4 font-medium text-slate-500 w-3/8">Traditional Job Portals</th>
                    <th className="py-3 px-4 font-medium text-cyan-400 w-3/8">Nexgent Platform</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60 text-slate-300">
                  <tr className="hover:bg-slate-900/60 transition-colors">
                    <td className="py-3 px-4 font-semibold text-white">Visibility Model</td>
                    <td className="py-3 px-4 text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <XCircle className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                        <span>Public activity feeds</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-cyan-300 font-medium">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                        <span>Privacy-focused & anonymized</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-slate-900/60 transition-colors">
                    <td className="py-3 px-4 font-semibold text-white">Network Design</td>
                    <td className="py-3 px-4 text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <XCircle className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                        <span>Large open database</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-cyan-300 font-medium">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                        <span>Curated professional network</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-slate-900/60 transition-colors">
                    <td className="py-3 px-4 font-semibold text-white">Matching Focus</td>
                    <td className="py-3 px-4 text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <XCircle className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                        <span>Mass unstructured applications</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-cyan-300 font-medium">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                        <span>Targeted, high-intent connections</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-slate-900/60 transition-colors">
                    <td className="py-3 px-4 font-semibold text-white">Data Control</td>
                    <td className="py-3 px-4 text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <XCircle className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                        <span>Limited user profile control</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-cyan-300 font-medium">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                        <span>Granular visibility settings</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-slate-900/60 transition-colors">
                    <td className="py-3 px-4 font-semibold text-white">Target Audience</td>
                    <td className="py-3 px-4 text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <XCircle className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                        <span>Broad entry to mid-level market</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-cyan-300 font-medium">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                        <span>Experienced leadership professionals</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 6 */}
          <section id="our-approach" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">6. Our Approach</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Nexgent prioritizes network quality over raw application volume. We facilitate meaningful interactions by enforcing mandatory identity review, structured corporate recruiter verification, and a professional environment built on mutual trust.
            </p>
          </section>

          {/* Section 7 */}
          <section id="trust-security" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Lock className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">7. Verification, Security & Standards</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3.5 bg-slate-950/80 rounded-xl border border-slate-800 space-y-1">
                <div className="text-cyan-400 font-semibold">Verification Protocols</div>
                <div className="text-slate-400">Recruiter corporate domain checks and candidate credential reviews are conducted to safeguard platform standards.</div>
              </div>
              <div className="p-3.5 bg-slate-950/80 rounded-xl border border-slate-800 space-y-1">
                <div className="text-cyan-400 font-semibold">Community Standards</div>
                <div className="text-slate-400">All participating members agree to accurate professional disclosures, respectful engagement, and strict non-redistribution of candidate data.</div>
              </div>
            </div>
          </section>

          {/* Section 8 */}
          <section id="looking-ahead" className="scroll-mt-28 space-y-6 bg-slate-900/80 p-6 md:p-8 rounded-2xl border border-cyan-800/50 shadow-[0_0_30px_rgba(6,182,212,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-950 text-cyan-400 rounded-lg border border-cyan-800">
                <ArrowRight className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">8. Looking Ahead</h2>
                <p className="text-xs text-slate-400">Platform Vision & Evolution</p>
              </div>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed">
              As the hiring landscape continues to evolve, Nexgent remains committed to supporting senior talent and corporate organizations through a secure, privacy-conscious recruitment platform. Our objective continues to be facilitating trusted connections through purpose-built technology.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs pt-2">
              <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                <div className="text-slate-500 font-mono">OPERATED BY</div>
                <div className="text-sm font-bold text-white">Motherson Infotech</div>
                <div className="text-slate-400">Nexgent Engineering Division</div>
              </div>

              <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                <div className="text-slate-500 font-mono">LOCATION</div>
                <div className="text-sm font-bold text-cyan-400">Pune, Maharashtra, India</div>
                <div className="text-slate-400">Support: support@nexgent.tech</div>
              </div>
            </div>

            <div className="p-3 bg-slate-950/40 rounded-lg border border-slate-800/80 text-center text-xs text-cyan-500 font-mono">
              {/* © {new Date().getFullYear()} Motherson Infotech / Nexgent. All rights reserved. */}
              Nexgent Trust & Governance Framework • Verified Profiles • Private Access • Privacy First

            </div>
          </section>

        </div>
      </main>
      <LandingFooter/>
    </div>
  );
};

export default AboutUs;